/**
 * Performance Calculator Utilities
 *
 * This module provides utilities for calculating financial performance metrics
 * from fund history data. Uses simple-statistics for mathematical calculations.
 */

import { standardDeviation, mean } from 'simple-statistics';
import {
  FundHistoryResponse,
  PerformanceMetrics,
  MetricsOptions,
  FundType
} from '../types';

/**
 * Performance calculator for fund metrics
 *
 * Provides static methods for calculating various financial performance metrics
 * including returns, volatility, and risk-adjusted measures.
 */
export class PerformanceCalculator {
  /**
   * Calculate performance metrics for multiple funds
   *
   * @param fundHistory - Array of fund history data
   * @param options - Configuration options for calculations
   * @returns Array of performance metrics for each fund
   */
  static calculateMetrics(
    fundHistory: FundHistoryResponse[],
    options: MetricsOptions = {}
  ): PerformanceMetrics[] {
    const {
      riskFreeRate = 0,
      frequency = 'daily',
      method = 'simple',
      minObservations = 30
    } = options;

    // Group data by fund code
    const fundGroups = this.groupByFund(fundHistory);
    const periodsPerYear = this.getPeriodsPerYear(frequency);

    const results: PerformanceMetrics[] = [];

    for (const [fundCode, data] of fundGroups) {
      // Sort by date to ensure chronological order
      const sortedData = data.sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      if (sortedData.length < minObservations) {
        results.push(this.createEmptyMetrics(fundCode, sortedData[0]));
        continue;
      }

      const prices = sortedData.map(d => d.price);
      const returns = this.calculateReturns(prices, method);

      if (returns.length === 0) {
        results.push(this.createEmptyMetrics(fundCode, sortedData[0]));
        continue;
      }

      const cumulativeReturn = this.calculateCumulativeReturn(returns, method);
      const annualizedReturn = this.calculateAnnualizedReturn(returns, periodsPerYear, method);
      const annualizedVolatility = this.calculateVolatility(returns, periodsPerYear);
      const sharpeRatio = this.calculateSharpeRatio(returns, riskFreeRate, periodsPerYear, method);

      results.push({
        fundCode,
        fundName: sortedData[0].fundName,
        fundType: sortedData[0].fundType,
        observations: prices.length,
        cumulativeReturn,
        annualizedReturn,
        annualizedVolatility,
        sharpeRatio
      });
    }

    return results;
  }

  /**
   * Group fund history data by fund code
   *
   * @param fundHistory - Array of fund history data
   * @returns Map of fund code to fund data array
   */
  static groupByFund(fundHistory: FundHistoryResponse[]): Map<string, FundHistoryResponse[]> {
    const groups = new Map<string, FundHistoryResponse[]>();

    for (const data of fundHistory) {
      const existing = groups.get(data.fundCode) || [];
      existing.push(data);
      groups.set(data.fundCode, existing);
    }

    return groups;
  }

  /**
   * Calculate returns from price series
   *
   * @param prices - Array of prices in chronological order
   * @param method - Return calculation method
   * @returns Array of returns
   */
  static calculateReturns(prices: number[], method: 'simple' | 'logarithmic'): number[] {
    if (prices.length < 2) return [];

    const returns: number[] = [];

    for (let i = 1; i < prices.length; i++) {
      const currentPrice = prices[i];
      const previousPrice = prices[i - 1];

      // Skip if previous price is invalid (zero or negative)
      if (previousPrice <= 0) continue;

      // Skip if current price is invalid (zero or negative)
      if (currentPrice <= 0) continue;

      if (method === 'logarithmic') {
        returns.push(Math.log(currentPrice / previousPrice));
      } else {
        returns.push((currentPrice / previousPrice) - 1);
      }
    }

    return returns;
  }

  /**
   * Calculate cumulative return over the period
   *
   * @param returns - Array of returns
   * @param method - Return calculation method
   * @returns Cumulative return
   */
  static calculateCumulativeReturn(returns: number[], method: string): number {
    if (returns.length === 0) return 0;

    if (method === 'logarithmic') {
      const sum = returns.reduce((acc, ret) => acc + ret, 0);
      return Math.exp(sum) - 1;
    } else {
      const product = returns.reduce((acc, ret) => acc * (1 + ret), 1);
      return product - 1;
    }
  }

  /**
   * Calculate annualized return
   *
   * @param returns - Array of returns
   * @param periodsPerYear - Number of periods per year
   * @param method - Return calculation method
   * @returns Annualized return
   */
  static calculateAnnualizedReturn(
    returns: number[],
    periodsPerYear: number,
    method: string
  ): number {
    if (returns.length === 0) return 0;

    const years = returns.length / periodsPerYear;
    if (years <= 0) return 0;

    if (method === 'logarithmic') {
      const sum = returns.reduce((acc, ret) => acc + ret, 0);
      return Math.exp(sum / years) - 1;
    } else {
      const product = returns.reduce((acc, ret) => acc * (1 + ret), 1);
      return Math.pow(product, 1 / years) - 1;
    }
  }

  /**
   * Calculate annualized volatility
   *
   * @param returns - Array of returns
   * @param periodsPerYear - Number of periods per year
   * @returns Annualized volatility
   */
  static calculateVolatility(returns: number[], periodsPerYear: number): number {
    if (returns.length < 2) return 0;

    const stdDev = standardDeviation(returns);
    return stdDev * Math.sqrt(periodsPerYear);
  }

  /**
   * Calculate Sharpe ratio
   *
   * @param returns - Array of returns
   * @param riskFreeRate - Risk-free rate
   * @param periodsPerYear - Number of periods per year
   * @param method - Return calculation method
   * @returns Sharpe ratio
   */
  static calculateSharpeRatio(
    returns: number[],
    riskFreeRate: number,
    periodsPerYear: number,
    method: string
  ): number {
    const annualizedReturn = this.calculateAnnualizedReturn(returns, periodsPerYear, method);
    const volatility = this.calculateVolatility(returns, periodsPerYear);

    if (volatility === 0) return 0;

    return (annualizedReturn - riskFreeRate) / volatility;
  }

  /**
   * Calculate Sortino ratio (uses only downside volatility)
   *
   * @param returns - Array of returns
   * @param riskFreeRate - Risk-free rate
   * @param periodsPerYear - Number of periods per year
   * @param method - Return calculation method
   * @returns Sortino ratio
   */
  static calculateSortinoRatio(
    returns: number[],
    riskFreeRate: number,
    periodsPerYear: number,
    method: string
  ): number {
    const annualizedReturn = this.calculateAnnualizedReturn(returns, periodsPerYear, method);

    // Calculate downside deviation (only negative returns)
    const downsideReturns = returns.filter(ret => ret < 0);
    if (downsideReturns.length === 0) {
      // If no negative returns, Sortino is undefined (return high value or Infinity)
      return annualizedReturn > riskFreeRate ? Infinity : 0;
    }

    const downsideStdDev = standardDeviation(downsideReturns);
    const downsideVolatility = downsideStdDev * Math.sqrt(periodsPerYear);

    if (downsideVolatility === 0) return 0;

    return (annualizedReturn - riskFreeRate) / downsideVolatility;
  }

  /**
   * Calculate maximum drawdown
   *
   * @param prices - Array of prices in chronological order
   * @returns Maximum drawdown as a percentage (negative value)
   */
  static calculateMaximumDrawdown(prices: number[]): number {
    if (prices.length < 2) return 0;

    let maxDrawdown = 0;
    let peak = prices[0];

    for (let i = 1; i < prices.length; i++) {
      if (prices[i] > peak) {
        peak = prices[i];
      } else {
        const drawdown = (prices[i] - peak) / peak;
        if (drawdown < maxDrawdown) {
          maxDrawdown = drawdown;
        }
      }
    }

    return maxDrawdown;
  }

  /**
   * Calculate Calmar ratio (annualized return / maximum drawdown)
   *
   * @param returns - Array of returns
   * @param prices - Array of prices in chronological order
   * @param periodsPerYear - Number of periods per year
   * @param method - Return calculation method
   * @returns Calmar ratio
   */
  static calculateCalmarRatio(
    returns: number[],
    prices: number[],
    periodsPerYear: number,
    method: string
  ): number {
    const annualizedReturn = this.calculateAnnualizedReturn(returns, periodsPerYear, method);
    const maxDrawdown = Math.abs(this.calculateMaximumDrawdown(prices));

    if (maxDrawdown === 0) return 0;

    return annualizedReturn / maxDrawdown;
  }

  /**
   * Calculate Beta (sensitivity to benchmark/market movements)
   *
   * @param fundReturns - Array of fund returns
   * @param benchmarkReturns - Array of benchmark returns (must align with fund returns)
   * @returns Beta coefficient
   */
  static calculateBeta(fundReturns: number[], benchmarkReturns: number[]): number {
    if (fundReturns.length !== benchmarkReturns.length || fundReturns.length < 2) {
      return 0;
    }

    const fundMean = mean(fundReturns);
    const benchmarkMean = mean(benchmarkReturns);

    // Calculate covariance
    let covariance = 0;
    for (let i = 0; i < fundReturns.length; i++) {
      covariance += (fundReturns[i] - fundMean) * (benchmarkReturns[i] - benchmarkMean);
    }
    covariance /= fundReturns.length;

    // Calculate benchmark variance
    const benchmarkVariance = standardDeviation(benchmarkReturns) ** 2;

    if (benchmarkVariance === 0) return 0;

    return covariance / benchmarkVariance;
  }

  /**
   * Calculate Information Ratio (active return / tracking error)
   *
   * @param fundReturns - Array of fund returns
   * @param benchmarkReturns - Array of benchmark returns (must align with fund returns)
   * @param periodsPerYear - Number of periods per year
   * @returns Information Ratio
   */
  static calculateInformationRatio(
    fundReturns: number[],
    benchmarkReturns: number[],
    periodsPerYear: number
  ): number {
    if (fundReturns.length !== benchmarkReturns.length || fundReturns.length < 2) {
      return 0;
    }

    // Calculate active returns (excess returns)
    const activeReturns = fundReturns.map((fundRet, i) => fundRet - benchmarkReturns[i]);
    const activeReturnMean = mean(activeReturns);

    // Calculate tracking error (standard deviation of active returns)
    const trackingError = standardDeviation(activeReturns) * Math.sqrt(periodsPerYear);

    if (trackingError === 0) return 0;

    // Annualize active return
    const annualizedActiveReturn = activeReturnMean * periodsPerYear;

    return annualizedActiveReturn / trackingError;
  }

  /**
   * Calculate Treynor Ratio (excess return / beta)
   *
   * @param returns - Array of fund returns
   * @param benchmarkReturns - Array of benchmark returns (must align with fund returns)
   * @param riskFreeRate - Risk-free rate
   * @param periodsPerYear - Number of periods per year
   * @param method - Return calculation method
   * @returns Treynor Ratio
   */
  static calculateTreynorRatio(
    returns: number[],
    benchmarkReturns: number[],
    riskFreeRate: number,
    periodsPerYear: number,
    method: string
  ): number {
    const annualizedReturn = this.calculateAnnualizedReturn(returns, periodsPerYear, method);
    const beta = this.calculateBeta(returns, benchmarkReturns);

    if (beta === 0) return 0;

    return (annualizedReturn - riskFreeRate) / beta;
  }

  /**
   * Get periods per year for frequency
   *
   * @param frequency - Data frequency
   * @returns Number of periods per year
   */
  static getPeriodsPerYear(frequency: string): number {
    const mapping: Record<string, number> = {
      daily: 252,
      weekly: 52,
      monthly: 12,
      quarterly: 4,
      annual: 1
    };

    return mapping[frequency] || 252;
  }

  /**
   * Create empty metrics for insufficient data
   *
   * @param fundCode - Fund code
   * @param sample - Sample data point for fund info
   * @returns Empty performance metrics
   */
  static createEmptyMetrics(fundCode: string, sample: FundHistoryResponse): PerformanceMetrics {
    return {
      fundCode,
      fundName: sample?.fundName || '',
      fundType: sample?.fundType || FundType.YAT,
      observations: 0,
      cumulativeReturn: 0,
      annualizedReturn: 0,
      annualizedVolatility: 0,
      sharpeRatio: 0
    };
  }
}
